<script>
import { isEqual } from "lodash-es";

import ActionWithTooltip from "@/components/ActionWithTooltip.vue";
import InflectionSchemeLink from "@/components/article/InflectionSchemeLink.vue";
import AudioButton from "@/components/AudioButton.vue";
import IconGreenCheckmark from "@/components/icons/green/IconGreenCheckmark.vue";
import IconCopy from "@/components/icons/IconCopy.vue";
import IconWhiteChevronDown from "@/components/icons/white/IconWhiteChevronDown.vue";
import showInflectionSchemeButtonMixin from "@/components/mixins/article/dictsys/show-inflection-scheme-button";
import TouchOrMouseElement from "@/components/TouchOrMouseElement.vue";
import lang from "@/directives/lang";
import { MatomoHelper } from "@/helpers";
import { MatomoEventAction, MatomoEventCategory } from "@/types";

import ArticleContentMetadata from "./ArticleContentMetadata.vue";
import ArticleContentTranslationsExplanations from "./ArticleContentTranslationsExplanations.vue";
import Inflections from "./Inflections.vue";

export default {
  name: "ArticleContentTranslations",
  components: {
    ArticleContentMetadata,
    ActionWithTooltip,
    ArticleContentTranslationsExplanations,
    AudioButton,
    IconGreenCheckmark,
    IconCopy,
    IconWhiteChevronDown,
    Inflections,
    InflectionSchemeLink,
    TouchOrMouseElement
  },
  directives: {
    lang
  },
  mixins: [showInflectionSchemeButtonMixin],
  props: {
    articleId: {
      type: String,
      required: true
    },
    translations: {
      type: Array,
      required: true
    },
    hasReadingLines: {
      type: Boolean,
      // eslint-disable-next-line vue/no-boolean-default
      default: true
    },
    expandedDefault: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      groupedTranslations: [],
      headerHover: true
    };
  },
  mounted() {
    this.groupedTranslations = this.makeGroupedTranslations();
  },
  methods: {
    makeGroupedTranslations() {
      const addTranslationToGroups = (groups, translation) => {
        groups.push({
          copySuccess: false,
          expanded: this.expandedDefault || translation.indexHit,
          id: translation.id,
          translation: translation.translation,
          pronunciationFileUrl: translation.pronunciationFileUrl,
          metadata: translation.metadata,
          wordclass: translation.wordclass,
          grammaticalComment: translation.grammaticalComment,
          inflections: translation.inflections,
          inflectionScheme: translation.inflectionScheme,
          explanations: []
        });

        return addExplanationToTranslation(groups, groups.length - 1, translation);
      };

      const addExplanationToTranslation = (groups, translationIndex, translation) => {
        if (
          translation.metadata.categories.length > 0 ||
          translation.metadata.usages.length > 0 ||
          translation.explanation ||
          translation.examples.length > 0
        ) {
          groups[translationIndex].explanations.push({
            categories: translation.metadata.categories,
            usages: translation.metadata.usages,
            explanation: translation.explanation,
            examples: translation.examples
          });
        }

        return groups;
      };

      const addExamplesToExplanation = (groups, translationIndex, explanationIndex, examples) => {
        groups[translationIndex].explanations[explanationIndex].examples =
          groups[translationIndex].explanations[explanationIndex].examples.concat(examples);

        return groups;
      };

      const findTranslationIndex = (groups, translation) =>
        groups.findIndex(
          (g) =>
            g.translation === translation.translation &&
            isEqual(
              g.inflections.long.slice().sort(),
              translation.inflections.long.slice().sort()
            ) &&
            isEqual(g.metadata, translation.metadata) &&
            isEqual(g.wordclass, translation.wordclass) &&
            g.grammaticalComment === translation.grammaticalComment
        );

      const findExplanationIndex = (explanations, translation) =>
        explanations.findIndex((e) => e.explanation === translation.explanation);

      return this.translations.reduce((groups, translation) => {
        const translationIndex = findTranslationIndex(groups, translation);

        // Not same translation, metadata or inflections,
        // so we have to add a new translation entry
        if (translationIndex === -1) {
          return addTranslationToGroups(groups, translation);
        }

        const explanationIndex = findExplanationIndex(
          groups[translationIndex].explanations,
          translation
        );

        // If no explanations are the same, we can add a new one
        if (explanationIndex === -1) {
          return addExplanationToTranslation(groups, translationIndex, translation);
        }

        return addExamplesToExplanation(
          groups,
          translationIndex,
          explanationIndex,
          translation.examples
        );
      }, []);
    },
    disableHeaderHover() {
      this.headerHover = false;
    },
    enableHeaderHover() {
      this.headerHover = true;
    },
    onToggleExpand(event, translation) {
      event.stopPropagation();
      event.preventDefault();

      translation.expanded = !translation.expanded;
    },
    async onTranslationClick(translation) {
      try {
        await navigator.clipboard.writeText(translation.translation);
        translation.copySuccess = true;
      } catch (e) {
        // Ignore
      }
    },
    resetTranslationCopySuccess(translation) {
      translation.copySuccess = false;
    },
    trackPronunciationClick() {
      MatomoHelper.sendEvent(
        MatomoEventCategory.Results,
        MatomoEventAction.ClickPronunciation,
        "Pronunciation on translation"
      );
    },
    trackInflectionSchemeClick() {
      MatomoHelper.sendEvent(
        MatomoEventCategory.Results,
        MatomoEventAction.ClickInflectionScheme,
        "Inflection scheme on translation"
      );
    }
  }
};
</script>

<template>
  <div class="article-content__translations">
    <div
      v-for="(translation, index) in groupedTranslations"
      :key="index"
      class="indentation-container"
    >
      <div
        class="article-content__translations-header"
        :class="{
          'article-content__translations-header--reading-lines': hasReadingLines,
          'article-content__translations-header--with-hover':
            translation.explanations.length > 0 && headerHover
        }"
        @click="onToggleExpand($event, translation)"
      >
        <span
          class="article-list-bullet article-list-bullet--primary article-list-bullet--prominent"
        >
          {{ index + 1 }}.
        </span>
        <span class="article-content__translations-header-content">
          <TouchOrMouseElement>
            <template #mouse>
              <ActionWithTooltip
                :margin-bottom="10"
                :show-success="translation.copySuccess"
                @hide="
                  resetTranslationCopySuccess(translation);
                  enableHeaderHover();
                "
                @show="disableHeaderHover()"
              >
                <template #element>
                  <span
                    v-lang:target
                    class="article-content__text article-content__text--translation article-content__text--primary article-content__text--prominent article-content__text--with-action"
                    @click.stop="onTranslationClick(translation)"
                  >
                    {{ translation.translation }}
                  </span>
                </template>
                <template #actionInitial>
                  <IconCopy class="article-content__translations-header-tooltip-icon mr-1" />
                  <span>{{ $t("article.actions.copy.copy") }}</span>
                </template>
                <template #actionSuccess>
                  <IconGreenCheckmark
                    class="article-content__translations-header-tooltip-icon mr-1"
                  ></IconGreenCheckmark
                  >{{ $t("article.actions.copy.copied") }}
                </template>
              </ActionWithTooltip>
            </template>
            <template #touch>
              <span
                v-lang:target
                class="article-content__text article-content__text--primary article-content__text--prominent"
              >
                {{ translation.translation }}
              </span>
            </template>
          </TouchOrMouseElement>

          <span class="article-content__translations-header-button-group ml-1">
            <TouchOrMouseElement
              v-if="showInflectionSchemeButton(translation)"
              class="d-inline-flex ml-1"
            >
              <template #mouse>
                <ActionWithTooltip
                  hide-on-click
                  @hide="enableHeaderHover()"
                  @show="disableHeaderHover()"
                >
                  <template #element>
                    <InflectionSchemeLink
                      :word="translation.translation"
                      :article-id="articleId"
                      :translation-id="translation.inflectionScheme.id"
                      :section-id="index"
                      @click="trackInflectionSchemeClick()"
                    />
                  </template>
                  <template #actionInitial>{{ $t("article.actions.conjugation.open") }}</template>
                </ActionWithTooltip>
              </template>
              <template #touch>
                <InflectionSchemeLink
                  :word="translation.translation"
                  :article-id="articleId"
                  :translation-id="translation.inflectionScheme.id"
                  :section-id="index"
                  @click="trackInflectionSchemeClick()"
                />
              </template>
            </TouchOrMouseElement>
            <TouchOrMouseElement v-if="translation.pronunciationFileUrl" class="d-inline-flex ml-1">
              <template #mouse>
                <ActionWithTooltip
                  hide-on-click
                  @hide="enableHeaderHover()"
                  @show="disableHeaderHover()"
                >
                  <template #element>
                    <AudioButton
                      :audio-url="translation.pronunciationFileUrl"
                      :aria-label="$t('article.pronunciation.label')"
                      @click="trackPronunciationClick()"
                  /></template>
                  <template #actionInitial>{{ $t("article.pronunciation.label") }}</template>
                </ActionWithTooltip>
              </template>
              <template #touch>
                <AudioButton
                  :audio-url="translation.pronunciationFileUrl"
                  :aria-label="$t('article.pronunciation.label')"
                  @click="trackPronunciationClick()"
                />
              </template>
            </TouchOrMouseElement>
          </span>

          <i
            v-if="translation.metadata.varieties.length > 0"
            v-lang:da
            class="article-content__text article-content__text--muted ml-2"
            >({{ translation.metadata.varieties.join(", ") }})</i
          >
          <Inflections
            v-if="!showInflectionSchemeButton(translation)"
            :inflections="translation.inflections.long"
            lang="target"
            class="article-content__inflections ml-2"
          />
          <span class="ml-2"></span>
          <ArticleContentMetadata :dictsys-article="translation" />
        </span>
        <button
          v-if="translation.explanations.length > 0"
          :aria-label="translation.expanded ? $t('article.contract') : $t('article.expand')"
          :aria-expanded="translation.expanded"
          class="article-content__drop-down article-content__drop-down--right"
          :class="{
            'article-content__drop-down--expanded': translation.expanded
          }"
          @click.stop="onToggleExpand($event, translation)"
        >
          <IconWhiteChevronDown role="presentation" class="article-content__drop-down-icon" />
        </button>
      </div>
      <ArticleContentTranslationsExplanations
        v-if="translation.expanded"
        :explanations="translation.explanations"
      />
    </div>
  </div>
</template>
